// Shared announcement popup card: live shop and Announcements editor preview.
// Splash uses Johnston shop type, Alegarta smoke (navbar overlay pattern), Add to cart + View product.

import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import {
    EMAIL_LOGO_URL,
    LSC_ANNOUNCEMENT,
    shouldUseBrandCtaGradient,
} from '../utils/announcementBrandStyles';
import { isProductSplashAnnouncement } from '../utils/productSplashContract';
import {
    buildProductSplashHeroModel,
    getProductSplashMotionClass,
    resolveAnnouncementDisplayImageUrl,
} from '../utils/productSplashCopy';
import {
    buildAnnouncementImageFitStyle,
    resolveAnnouncementImageFitFromDoc,
} from '../utils/announcementImageFit';

function SplashParagraph({ text }) {
    const lines = String(text || '').split('\n');
    return (
        <p className="lsc-splash-body">
            {lines.map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                </React.Fragment>
            ))}
        </p>
    );
}

function ProductSplashHero({
    model,
    preview,
    onClose,
    onAddToCart,
    onViewProduct,
    addBusy,
}) {
    const viewIsPrimary = model.showViewProduct && !model.showAddToCart;
    const addHandler = preview ? undefined : onAddToCart;
    const viewHandler = preview ? undefined : onViewProduct;
    const closeHandler = preview ? undefined : onClose;

    const heroFitStyle = buildAnnouncementImageFitStyle(model.imageFit);
    const backgroundFitStyle = buildAnnouncementImageFitStyle(model.backgroundImageFit);
    const heroInWell = model.hasHeroImage
        ? { src: model.imageUrl, style: heroFitStyle, alt: model.headline || 'New product' }
        : model.hasBackgroundImage
            ? { src: model.backgroundImageUrl, style: backgroundFitStyle, alt: model.headline || 'New product' }
            : null;
    const showCardBackground = model.hasBackgroundImage && model.hasHeroImage;

    return (
        <>
            {showCardBackground ? (
                <div className="lsc-splash-bg" aria-hidden="true">
                    <img
                        src={model.backgroundImageUrl}
                        alt=""
                        className="lsc-announcement-fit-img"
                        style={backgroundFitStyle}
                    />
                </div>
            ) : null}

            <button
                type="button"
                className="announcement-close-btn lsc-announcement-close lsc-announcement-close--splash"
                onClick={closeHandler}
                title="Close"
                aria-label="Close"
            >
                <CloseOutlined />
            </button>

            {heroInWell ? (
                <div className="lsc-splash-hero">
                    <img
                        src={heroInWell.src}
                        alt={heroInWell.alt}
                        className="lsc-splash-hero-img"
                        style={heroInWell.style}
                    />
                    <span className="lsc-splash-smoke" aria-hidden="true" />
                </div>
            ) : (
                <>
                    {model.showBrandStrip ? (
                        <div className="lsc-announcement-brand-strip lsc-splash-brand-strip">
                            <span className="lsc-splash-smoke" aria-hidden="true" />
                            <img src={EMAIL_LOGO_URL} alt="" className="lsc-announcement-logo lsc-announcement-logo--sm" />
                            <p className="lsc-announcement-brand-caption">London Smoke &amp; Cure</p>
                        </div>
                    ) : (
                        <span className="lsc-splash-smoke" aria-hidden="true" />
                    )}
                </>
            )}

            <div className="lsc-splash-copy">
                {model.eyebrow ? <p className="lsc-splash-kicker">{model.eyebrow}</p> : null}
                <h2 className="lsc-splash-title">{model.headline || 'New product'}</h2>
                {model.paragraphs.map((para, i) => (
                    <SplashParagraph key={i} text={para} />
                ))}
                {model.footnote ? <p className="lsc-splash-footnote">{model.footnote}</p> : null}

                <div className="lsc-splash-actions">
                    {model.showAddToCart ? (
                        <button
                            type="button"
                            className="announcement-popup-cta announcement-popup-cta--splash"
                            onClick={addHandler}
                            disabled={!preview && addBusy}
                        >
                            {addBusy && !preview ? 'Adding' : model.addToCartLabel}
                        </button>
                    ) : null}
                    {model.showViewProduct ? (
                        <button
                            type="button"
                            className={
                                viewIsPrimary
                                    ? 'announcement-popup-cta announcement-popup-cta--splash'
                                    : 'lsc-splash-view'
                            }
                            onClick={viewHandler}
                        >
                            {model.viewProductLabel}
                        </button>
                    ) : null}
                    <button type="button" className="lsc-splash-continue" onClick={closeHandler}>
                        {model.continueLabel}
                    </button>
                </div>
            </div>
        </>
    );
}

function GenericAnnouncementCard({
    announcement,
    displayTitle,
    displayMessage,
    displayCtaText,
    preview,
    onClose,
    onCta,
}) {
    const {
        bgColor,
        textColor,
        borderColor,
        borderWidth,
        borderRadius,
        fontSize,
        titleFontSize,
        textAlign,
        fontWeight,
        padding,
        maxWidth,
        ctaColor,
        showIcon,
        icon,
        backgroundImageUrl,
        showLogo,
    } = announcement;

    const imageUrl = resolveAnnouncementDisplayImageUrl(announcement);
    const displayLogo = showLogo !== false;
    const bodyText = textColor || LSC_ANNOUNCEMENT.text;
    const cardBorder = borderColor || LSC_ANNOUNCEMENT.border;
    const cardRadius = borderRadius ?? 12;
    const cardPad = padding ?? 24;
    const cardMax = maxWidth ?? 520;
    const ctaGradient = shouldUseBrandCtaGradient(ctaColor);
    const backgroundFitStyle = buildAnnouncementImageFitStyle(
        resolveAnnouncementImageFitFromDoc(announcement, 'backgroundImage')
    );

    const cardStyle = {
        maxWidth: `${cardMax}px`,
        borderRadius: `${cardRadius}px`,
        border: `${borderWidth ?? 1}px solid ${cardBorder}`,
        overflow: 'hidden',
        boxShadow: 'var(--customer-shadow-2, 0 6px 14px rgba(0, 0, 0, 0.06))',
        backgroundColor: backgroundImageUrl ? undefined : '#ffffff',
        position: 'relative',
    };

    const scrimStyle = backgroundImageUrl
        ? { backgroundColor: `${bgColor || '#ffffff'}e6` }
        : null;

    const innerPadStyle = {
        paddingLeft: `${cardPad}px`,
        paddingRight: `${cardPad}px`,
        paddingBottom: `${cardPad}px`,
        paddingTop: backgroundImageUrl
            ? `${cardPad + (displayLogo ? 52 : 0)}px`
            : `${cardPad}px`,
        textAlign: textAlign || 'center',
        fontWeight: fontWeight || 'normal',
        fontFamily: LSC_ANNOUNCEMENT.bodyFont,
        color: bodyText,
        position: 'relative',
        zIndex: 1,
        backgroundColor: backgroundImageUrl ? 'transparent' : bgColor || LSC_ANNOUNCEMENT.popupBodyBg,
    };

    const closeHandler = preview ? undefined : onClose;
    const ctaHandler = preview ? undefined : onCta;
    const showCta = Boolean(displayCtaText);

    return (
        <div
            className="lsc-announcement-popup announcement-popup-display"
            style={cardStyle}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal={preview ? undefined : 'true'}
            aria-label={displayTitle || 'Announcement'}
        >
            <button
                type="button"
                className="announcement-close-btn lsc-announcement-close"
                onClick={closeHandler}
                title="Close"
                aria-label="Close"
            >
                <CloseOutlined />
            </button>

            {backgroundImageUrl ? (
                <div className="lsc-announcement-bg-photo" aria-hidden="true">
                    <img
                        src={backgroundImageUrl}
                        alt=""
                        className="lsc-announcement-fit-img"
                        style={backgroundFitStyle}
                    />
                </div>
            ) : null}

            {backgroundImageUrl && scrimStyle ? (
                <div className="lsc-announcement-bg-scrim" style={scrimStyle} />
            ) : null}

            {!backgroundImageUrl && displayLogo ? (
                <div className="lsc-announcement-brand-strip">
                    <img src={EMAIL_LOGO_URL} alt="" className="lsc-announcement-logo" />
                    <p className="lsc-announcement-brand-caption">London Smoke &amp; Cure - wholesale</p>
                </div>
            ) : null}

            {backgroundImageUrl && displayLogo ? (
                <div className="lsc-announcement-brand-floating">
                    <img src={EMAIL_LOGO_URL} alt="" className="lsc-announcement-logo lsc-announcement-logo--sm" />
                    <span className="lsc-announcement-brand-caption lsc-announcement-brand-caption--light">
                        London Smoke &amp; Cure - wholesale
                    </span>
                </div>
            ) : null}

            <div className="lsc-announcement-body" style={innerPadStyle}>
                {imageUrl ? (
                    <img src={imageUrl} alt="" className="announcement-popup-image lsc-announcement-inline-image" />
                ) : null}

                <div className="announcement-popup-content">
                    <div className="announcement-popup-header">
                        {showIcon !== false && icon ? (
                            <span className="announcement-popup-icon" aria-hidden>
                                {icon}
                            </span>
                        ) : null}
                        <h2
                            className="lsc-announcement-title"
                            style={{
                                fontSize: `${titleFontSize || 24}px`,
                                margin: 0,
                                fontFamily: LSC_ANNOUNCEMENT.titleFont,
                                color: bodyText,
                            }}
                        >
                            {displayTitle}
                        </h2>
                    </div>

                    <p
                        className="lsc-announcement-message"
                        style={{
                            fontSize: `${fontSize || 16}px`,
                            margin: '16px 0',
                            lineHeight: 1.6,
                            fontFamily: LSC_ANNOUNCEMENT.bodyFont,
                            color: LSC_ANNOUNCEMENT.textMuted,
                        }}
                    >
                        {displayMessage}
                    </p>

                    {showCta ? (
                        <button
                            type="button"
                            className={
                                ctaGradient
                                    ? 'announcement-popup-cta announcement-popup-cta--brand'
                                    : 'announcement-popup-cta announcement-popup-cta--custom'
                            }
                            style={
                                ctaGradient
                                    ? undefined
                                    : {
                                          backgroundColor: ctaColor || LSC_ANNOUNCEMENT.accent,
                                          color: '#ffffff',
                                      }
                            }
                            onClick={ctaHandler}
                        >
                            {displayCtaText}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function AnnouncementPopupCard({
    announcement,
    displayTitle,
    displayMessage,
    displayCtaText,
    visible = true,
    contained = false,
    preview = false,
    reducedMotion = false,
    position = 'center',
    animation = 'fadeIn',
    onClose,
    onAddToCart,
    onViewProduct,
    onCta,
    addBusy = false,
}) {
    if (!announcement) return null;

    const isSplash = isProductSplashAnnouncement(announcement);
    const title = displayTitle != null ? displayTitle : announcement.title;
    const message = displayMessage != null ? displayMessage : announcement.message;
    const ctaText = displayCtaText != null ? displayCtaText : announcement.ctaText;

    const overlayClass = [
        'announcement-overlay',
        visible ? 'visible' : '',
        position || 'center',
        isSplash ? 'announcement-overlay--splash' : '',
        contained ? 'announcement-overlay--preview' : '',
        preview ? 'announcement-overlay--editor' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const motionClass = isSplash ? getProductSplashMotionClass(reducedMotion) : '';

    const overlayClick = preview || contained ? undefined : onClose;

    if (isSplash) {
        const model = buildProductSplashHeroModel({
            ...announcement,
            title,
            message,
            ctaText,
        });
        const splashFrameClass = [
            'lsc-announcement-popup',
            'announcement-popup-display',
            'lsc-splash-card',
            preview ? 'lsc-splash-card--preview' : '',
            visible && !preview ? 'lsc-splash-motion' : '',
            motionClass,
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div
                className={overlayClass}
                onClick={overlayClick}
                data-announcement-overlay="true"
                data-product-splash="true"
            >
                <div
                    className={splashFrameClass}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal={preview ? undefined : 'true'}
                    aria-label={model.headline || 'New product'}
                >
                    <ProductSplashHero
                        model={model}
                        preview={preview}
                        onClose={onClose}
                        onAddToCart={onAddToCart}
                        onViewProduct={onViewProduct || onCta}
                        addBusy={addBusy}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={overlayClass}
            onClick={overlayClick}
            data-announcement-overlay="true"
        >
            <div className={`announcement-popup-display ${animation || 'fadeIn'}`} style={{ width: '100%' }}>
                <GenericAnnouncementCard
                    announcement={announcement}
                    displayTitle={title}
                    displayMessage={message}
                    displayCtaText={ctaText}
                    preview={preview}
                    onClose={onClose}
                    onCta={onCta || onViewProduct}
                />
            </div>
        </div>
    );
}
